
import { Component, Vue, Prop, InjectReactive, Inject } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"
import moment from "moment"
import { cloneDeep } from "lodash"

import vSelect from "vue-select"
import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"
import DataSourceTemplate from "@/components/Fields/DataSourceTemplate.vue"
import DateTime from "@/components/Fields/DateTime.vue"
import Tags from "@/components/Fields/Tags.vue"
import Numeric from "@/components/Fields/Numeric.vue"
import UserRole from "@/components/CaseDetails//Users/UserRole.vue"
import FormGroup from "@/components/Fields/FormGroup.vue"
import ExternalLink from "@/components/Fields/ExternalLink.vue"
import UniqueCaseNumber from "@/components/Fields/CaseNumber.vue"
import DateTimezoneInfo from "@/components/Fields/DateTimezoneInfo.vue"

import CaseFileUpload from "@/components/CaseDetails/Files/CaseFileUpload.vue"
import CaseFileDisplay from "@/components/CaseDetails/Files/CaseFileDisplay.vue"
import DataDetailsModal from "@/components/CaseDetails/DataDetailsModal.vue"
import MapLocation from "@/components/Fields/MapLocation.vue"
import Metrics from "@/components/Fields/Metrics.vue"
import AreaSelect from "@/components/Fields/AreaSelect.vue"

import { CaseDetails, Team, Field, CaseField, FieldInstance, CalculationType,
	CalculationDataTypeValue, DataType, Permission, TemplateDetails, CaseAudit, Status, Severity } from "@/types"
import CaseFile from "@/types/caseFile"
import { CurrentUser } from "@/types/user"
import { InUseBy } from "@/types/caseField"

import CaseFieldValidation from "@/scripts/validation/caseFieldValidation"
import { normalizeCaseCadenceProperties } from "@/scripts/caseDetails"

import api from "@/services/api"

import { LayoutLocations } from "@/types/templateLayout"
import TimeSpentDisplay from "@/components/CaseDetails/TimeSpentDisplay.vue"
import HubCaseService from "@/services/hubCaseService"
import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"
import AssignedTime from "@/components/CaseDetails/Users/AssignedTime.vue"

import { ui } from "@/types/ui"
import { Settings } from "luxon"
import RecordAssignmentService from "@/services/recordAssignmentService"
import { DataObjectMethods, getPermissionTeams, hasPermission, hasViewPermission } from "@/scripts/caseFields"
import { TenantFeatures } from "@/types/tenantFeatures"
import { isBuiltInCaseColumn, getBuiltInCaseColumn, CaseColumn } from "@/types/caseColumns"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"text-area-expand": TextAreaToggleExpand,
		"file-upload": CaseFileUpload,
		"case-file": CaseFileDisplay,
		"data-select": DataSourceSelect,
		"data-template": DataSourceTemplate,
		"tags": Tags,
		"data-details": DataDetailsModal,
		"form-group": FormGroup,
		"numeric": Numeric,
		"date-time": DateTime,
		"map-location": MapLocation,
		"user-role": UserRole,
		"external-link": ExternalLink,
		"case-number": UniqueCaseNumber,
		"time-spent-display": TimeSpentDisplay,
		"timezone-info": DateTimezoneInfo,
		"assigned-time": AssignedTime,
		"metrics": Metrics,
		"area-select": AreaSelect
	}
})
export default class CaseFields extends Vue {
	$refs!: {
		viewItemDetails: DataDetailsModal;
	}
	@InjectReactive("hubCaseService") hubCaseService!: HubCaseService;
	@Inject({ from: "recordAssignmentService", default: null }) recordAssignmentService!: RecordAssignmentService

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("getMyId")
	public myId!: string;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@caseStore.Getter("getStatuses")
	private readonly statuses!: Status[]

	@caseStore.Getter("getSeverities")
	private readonly severities!: Severity[]

	@Prop({ default: () => [] })
	public caseFiles!: CaseFile[];

	@caseStore.Getter("getPendingUploadFiles")
	pendingUploadFiles!: any[];

	@caseStore.State
	public fieldsAwaitingUpdate!: string[] | null

	@caseStore.Mutation
	cascadeListChange!: (args: { caseDetails: CaseDetails, caseField: CaseField, newValue: any, getFields: boolean }) => void

	@caseStore.Mutation
	setFieldAwaitingUpdate!: (fieldId: string) => void

	@caseStore.Mutation
	private removeFieldAwaitingUpdate!: (fieldId: string) => void

	public dataSourceOptions: any[] = [];

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	@Prop({ default: null })
	public layout!: LayoutLocations | null;

	@Prop({ default: false })
	public canEditCurrentCase!: boolean

	@Prop()
	public currentCase!: CaseDetails;

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop({ default: null })
	caseTemplate!: TemplateDetails | null

	@Prop({ type: Boolean, default: true })
	displaySectionTitles!: boolean

	@Prop({ type: Boolean, default: false })
	displayAllSections!: boolean

	@Prop({ type: Array, default: () => null })
	fieldsToDisplay!: string[] | null

	@Prop({ type: Array, default: () => null })
	hiddenFields!: string[] | null

	@Prop({ type: Array, default: () => null })
	fieldDisplayOrder!: string[] | null

	@Prop({ type: Boolean, default: false })
	updateCaseStateAfterChange!: boolean

	@Prop({ type: Boolean, default: true })
	displayUserAssignments!: boolean

	@Prop({ type: Boolean, default: false })
	public noPadding!: boolean

	ui = ui

	isBuiltInCaseColumn = isBuiltInCaseColumn
	getBuiltInCaseColumn = getBuiltInCaseColumn
	private hasViewPermission = hasViewPermission

	private builtInSpecialColumnIds: string[] = ["statusId", "severityId", "areas", "tags"]

	postInProgressDebounce: { [key: string]: NodeJS.Timeout } = {}
	savingFields: { [key: string]: boolean } = {}
	waitingFields: { [key: string]: boolean } = {}
	fieldErrors: { [key: string]: {
		error: string,
		clearTimeout: NodeJS.Timeout
	} } = {}
	builtInFieldInUseBy: { fieldId: string, inUseBy: InUseBy } | null = null

	public created () {
	}

	CalculationType = CalculationType

	public get builtInFieldIds (): string[] {
		if (!this.fieldsToDisplay) return []
		return this.fieldsToDisplay.filter((fieldId) => this.isBuiltInCaseColumn(fieldId))
	}

	public get sections (): string[] {
		if (this.fieldDisplayOrder && this.fieldsToDisplay && this.fieldsToDisplay.length > 0) {
			return ["Ordered"]
		}

		if (this.displayAllSections) {
			const allSections = this.currentCase ? this.currentCase.sections : []
			if (this.builtInFieldIds && this.builtInFieldIds.length > 0) {
				return [...allSections, "Built In"]
			} else {
				return allSections
			}
		}

		if (!this.currentCase || !this.currentCase.sections) {
			return []
		}

		if (this.currentCase.sectionDetails && this.layout !== null) {
			return this.currentCase.sections.filter(s => this.currentCase!.sectionDetails && this.currentCase!.sectionDetails[s]
				? this.layout === this.currentCase!.sectionDetails[s].layout
				: this.layout === LayoutLocations.Default)
		} else if (!this.currentCase.sectionDetails &&
			!(this.layout === LayoutLocations.Default || this.layout === null)) {
			return []
		}

		return this.currentCase.sections
	}

	public get viewableSections () {
		if (this.fieldDisplayOrder && this.fieldsToDisplay && this.fieldsToDisplay.length > 0) {
			return ["Ordered"]
		}

		if (!this.currentCase || !this.currentCase.fields) {
			return this.sections
		}

		return this.sections.filter(section => {
			const fields = this.caseFields[section]
			if (section === "Built In") {
				return Array.isArray(fields) && fields.length > 0
			}

			if (Array.isArray(fields)) {
				return fields.some(f => typeof f !== "string" && hasViewPermission(f, this.me, this.currentCase))
			}

			return false
		})
	}

	public get caseFields (): { [key:string]: (CaseField | string)[] } {
		if (!this.currentCase) {
			return {}
		}

		const allFields: CaseField[] = []
		const filterFieldsByEditable = !!this.fieldsToDisplay

		if (this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				if (!filterFieldsByEditable || (this.fieldsToDisplay && this.fieldsToDisplay.includes(fieldId))) {
					allFields.push(this.currentCase!.fields[fieldId])
				}
			})
		}

		allFields.forEach((field: CaseField) => {
			if (this.currentCase!.sections) {
				const sectionOrder = this.currentCase!.sections.indexOf(field.section)
				field.sectionOrder = sectionOrder === -1 ? 99999 : sectionOrder
			}
		})

		let caseFields: (CaseField | string)[] = allFields.sortByThen({ key: "sectionOrder" }, { thenKey: "order" })

		if (this.builtInFieldIds.length > 0) {
			caseFields = [...caseFields, ...this.builtInFieldIds]
		}

		// When both fieldDisplayOrder and fieldsToDisplay are specified, rearrange fields into a single "Ordered" section following the defined fieldDisplayOrder
		if (this.fieldDisplayOrder && this.fieldsToDisplay && this.fieldsToDisplay.length > 0) {
			const fieldMap: { [key: string]: CaseField | string } = {}
			caseFields.forEach((f: CaseField | string) => {
				const fieldId = typeof f === "string" ? f : f.field.id!
				fieldMap[fieldId] = f
			})

			const orderedFields: (CaseField | string)[] = []
			this.fieldDisplayOrder.forEach(fieldId => {
				if (fieldMap[fieldId]) {
					orderedFields.push(fieldMap[fieldId])
				}
			})

			return { "Ordered": orderedFields }
		}

		return caseFields.reduce((group: any, field: CaseField | string) => {
			let section: string = typeof field === "string" ? "Built In" : field.section ? field.section : "General";

			(group[section] = group[section] || []).push(field)
			return group
		}, {})
	}

	private get inProgressEnabled (): boolean {
		return !!this.currentCase.id && !!this.me?.enabledFeatures?.includes("inprogresssaving")
	}

	private async fieldGotFocus (field: CaseField | string, _event: FocusEvent) {
		const fieldId = typeof field === "string" ? field : field.field.id!
		const builtInField = getBuiltInCaseColumn(fieldId)
		const fieldName = builtInField ? builtInField.label : (typeof field === "string" ? field : field.field.title)

		try {
			if (this.currentCase!.id && !this.isFieldHidden(fieldId)) {
				if (!builtInField && typeof field !== "string" && !this.hasPermission(field)) {
					return
				}

				Vue.set(this.waitingFields, fieldId, true)
				const result = await api.selectCaseField(this.currentCase!.id, fieldId, true)

				if (this.updateCaseStateAfterChange) {
					if (builtInField) {
						this.builtInFieldInUseBy = { fieldId, inUseBy: { id: this.me.id, name: this.me.name } }
					} else if (typeof field !== "string") {
						if (field) {
							Vue.set(field, "inUseBy", { id: this.me.id, name: this.me.name })
						}
					}
				}

				if (!result.success) {
					window.blur()
					this.fieldSetError(fieldId, `Unable to edit ${fieldName} - In use by ${result.inUseByName}`)
				}
			}
		} catch (ex) {
			this.fieldSetError(fieldId, `Unable to edit ${fieldName} - ${ex}`)
			window.blur()
			console.error(ex)
		} finally {
			Vue.delete(this.waitingFields, fieldId)
		}
	}

	private async fieldLostFocus (field: CaseField, newValue: string): Promise<void> {
		const fieldId = typeof field === "string" ? field : field.field.id!
		const builtInField = getBuiltInCaseColumn(fieldId)
		const fieldName = builtInField ? builtInField.label : (typeof field === "string" ? field : field.field.title)

		try {
			if (this.currentCase!.id && (builtInField || this.hasPermission(field as CaseField))) {
				if (builtInField) {
					if (this.currentCase![fieldId]) {
						await this.saveBuiltInFieldChange(fieldId, newValue)
					} else {
						await api.selectCaseField(this.currentCase!.id, fieldId, false)
					}
				} else {
					if (field.oldValue !== newValue) {
						await this.saveFieldChange(field, newValue)
					} else {
						await api.selectCaseField(this.currentCase!.id, fieldId, false)
					}
				}
			}
		} catch (ex) {
			this.fieldSetError(fieldId, `Unable to edit ${fieldName} - ${ex}`)
		} finally {
			if (this.savingFields[fieldId]) {
				Vue.delete(this.savingFields, fieldId)
			}
		}
	}

	private async fieldSaveCurrentValue (caseField: CaseField) {
		if (this.currentCase!.id && this.hasPermission(caseField)) {
			await this.saveFieldChange(caseField, caseField.value)
		}
	}

	private fieldSetError (fieldId: string, error: string) {
		if (this.fieldErrors[fieldId]) {
			clearTimeout(this.fieldErrors[fieldId].clearTimeout)
		}

		Vue.set(this.fieldErrors, fieldId, {
			error,
			clearTimeout: setTimeout(() => {
				Vue.delete(this.fieldErrors, fieldId)
			}, 5000)
		})
	}

	private async dataListItemChanged (caseField: CaseField, newValue: any) {
		if (!this.currentCase.id) {
			caseField.label = newValue ? newValue.label : null
		}

		if (caseField.field.dataType !== "list" && caseField.field.dataType !== "list-multiple") return

		this.cascadeListChange({
			caseDetails: this.currentCase,
			caseField,
			newValue,
			getFields: false
		})
	}

	private async dateTimeFieldLostFocus (caseField: CaseField, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.hasPermission(caseField)) {
			if ((caseField.oldValue == null && newValue !== null) || (caseField.oldValue && !moment(caseField.oldValue!).isSame(newValue))) {
				await this.saveFieldChange(caseField, newValue)
			} else {
				await api.selectCaseField(this.currentCase!.id, caseField.field.id!, false)
			}
		} else if (!this.currentCase!.id) {
			Vue.set(caseField, "value", newValue)
		}
	}

	private async saveFieldChange (caseField: CaseField, newValue: string): Promise<void> {
		if (this.updateCaseStateAfterChange && caseField) {
			Vue.set(caseField, "inUseBy", null)
		}
		Vue.set(this.savingFields, caseField.field!.id!, true)
		try {
			if (this.me.tenantEnabledFeatures?.includes(TenantFeatures.UserRoleFieldHighLatencyUserEnhancements) &&
			this.me.settings?.settings["highLatencyMode"] &&
			caseField.field.dataType === "user-role") {
				if (caseField.field.id) {
					this.handleFieldAwaitingUpdate(caseField.field.id)
				}
			}
			const timezone = caseField.field.dataType === "date" || caseField.field.dataType === "date-time"
				? (Settings as any).defaultZone === "system"
					? Intl.DateTimeFormat().resolvedOptions().timeZone
					: (Settings as any).defaultZone.ianaName
				: null
			caseField.value = newValue
			await api.saveCaseField(this.currentCase!.id!, caseField.field.id!, caseField.value, timezone)
			caseField.oldValue = newValue
			const reloadDataTypes = ["user-role", "list", "list-multiple", "list-data", "date", "date-time"]
			if (this.updateCaseStateAfterChange && reloadDataTypes.includes(caseField.field.dataType!)) {
				this.currentCase = await api.caseDetails(this.currentCase.caseNumber!)
			}
			const deepCopiedCase = cloneDeep(this.currentCase)
			this.$emit("case-updated", deepCopiedCase, caseField)
		} catch (err) {
			console.error(err)
			if (caseField.field.id) {
				this.removeFieldAwaitingUpdate(caseField.field.id)
			}
			this.fieldSetError(caseField.field.id!, "Save failed")
		} finally {
			Vue.delete(this.savingFields, caseField.field!.id!)
		}
	}

	private async saveBuiltInFieldChange (fieldId: string, newValue: any): Promise<void> {
		Vue.set(this.savingFields, fieldId, true)
		try {
			if (fieldId === "cadenceEnabled") {
				normalizeCaseCadenceProperties(this.currentCase!, this.caseTemplate!, this.getIsFeatureEnabled)
				await api.saveCase(this.currentCase)
			}
			await api.saveCaseProperty(this.currentCase!.id!, fieldId, newValue)
			Vue.set(this.currentCase, fieldId, newValue)
			if (fieldId === "severityId") {
				const severityTitle = this.severities.find(severity => severity.id === newValue)!.title
				this.currentCase.severityTitle = severityTitle
			} else if (fieldId === "statusId") {
				const statusTitle = this.statuses.find(status => status.id === newValue)!.title
				this.currentCase.statusTitle = statusTitle
			}
			const deepCopiedCase = cloneDeep(this.currentCase)
			this.$emit("case-updated", deepCopiedCase, null)
		} catch (error) {
			this.fieldSetError(fieldId, "Save failed")
		} finally {
			this.builtInFieldInUseBy = null
			Vue.delete(this.savingFields, fieldId)
		}
	}

	private async addItem (caseField: CaseField, newValue: any): Promise<void> {
		if (!hasPermission(caseField, this.currentCase, this.canEditCurrentCase, this.me)) {
			return
		}
		return DataObjectMethods.addItem(caseField, newValue, this.currentCase)
	}
	private async updateItem (caseField: CaseField, updatedValue: any): Promise<void> {
		if (!hasPermission(caseField, this.currentCase, this.canEditCurrentCase, this.me)) {
			return
		}
		return DataObjectMethods.updateItem(caseField, updatedValue, this.currentCase)
	}
	private async removeItem (caseField: CaseField, newValue: any): Promise<void> {
		if (!hasPermission(caseField, this.currentCase, this.canEditCurrentCase, this.me)) {
			return
		}
		return DataObjectMethods.removeItem(caseField, newValue, this.currentCase)
	}

	private fieldFiles (fieldId: string): CaseFile[] {
		return this.caseFiles.filter(caseFile => caseFile.fieldId === fieldId)
	}

	private displayEditTeams (field: CaseField): string {
		const teams = getPermissionTeams(field, "edit", this.currentCase)
		if (!teams || teams.length === 0) {
			return ""
		}

		return teams.map(team => team.title).join(", ")
	}

	private hasPermission (field: CaseField): boolean {
		if (!this.canEditCurrentCase) {
			return false
		}
		if (field.field.displayReadOnly) {
			return false
		}

		const teams = getPermissionTeams(field, "edit", this.currentCase)
		if (!teams || teams.length === 0) {
			return true
		}

		return this.me.teams && teams.some(team => this.me.teams.find((myTeam: any) => myTeam.id === team.id))
	}

	/**
	 * Returns true if the given case field is currently being updated on the case. Returns false otherwise
	 * @param caseField The field to check
	 */
	private hasFieldsAwaitingUpdate (caseField: CaseField): boolean {
		if (this.me.tenantEnabledFeatures?.includes(TenantFeatures.UserRoleFieldHighLatencyUserEnhancements) &&
		this.me.settings?.settings["highLatencyMode"] &&
		caseField.field.id && this.fieldsAwaitingUpdate?.includes(caseField.field.id)) {
			return true
		}
		return false
	}

	private openNewItemModal (openArgs: any) {
		this.$refs.viewItemDetails.open(openArgs)
	}

	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) return false

		if (field.field.dataType?.toLowerCase() === "file-only") {
			let id = field.field.id !== undefined ? field.field.id : ""
			if (id === "" || this.fieldFiles(id).length === 0) {
				return true
			} else {
				return false
			}
		} else {
			return CaseFieldValidation.fieldRequired(field)
		}
	}

	private sectionDescription (section: string) {
		if (!this.currentCase?.sectionDetails || !this.currentCase.sectionDetails[section]) {
			return ""
		}

		return this.currentCase.sectionDetails[section].description
	}

	private setFieldCalculated (fieldInstance: FieldInstance, calculated: boolean) {
		const field = fieldInstance.field
		if (this.currentCase && this.currentCase.id) {
			api.setFieldCalculated(this.currentCase.id, field.id!, calculated)
		} else {
			if (calculated) {
				field.dataType = "calculated"
				fieldInstance.value = null
			} else if (field.dataType === "calculated") {
				const dataType = CalculationDataTypeValue[field.extensions!.calculationType as CalculationType]
				switch (dataType) {
					case DataType.string:
						field.dataType = "text"
						break
					case DataType.dateTime:
						field.dataType = "date"
						break
					case DataType.number:
						field.dataType = "number"
						break
					case DataType.boolean:
						field.dataType = "checkbox"
						break
				}
			}
		}
	}

	private isCheckboxCalculation (field: Field): boolean {
		return !!field.extensions &&
			CalculationDataTypeValue[field.extensions.calculationType as CalculationType] === DataType.boolean
	}

	private updateInProgress (fieldId: string, value: string) {
		if (!this.inProgressEnabled) return

		if (this.postInProgressDebounce[fieldId]) {
			clearTimeout(this.postInProgressDebounce[fieldId])
		}

		this.postInProgressDebounce[fieldId] = setTimeout(async () => {
			Vue.set(this.savingFields, fieldId, true)

			clearTimeout(this.postInProgressDebounce[fieldId])
			delete this.postInProgressDebounce[fieldId]
			if (!this.updateCaseStateAfterChange) {
				await this.hubCaseService.inProgressUpdate(this.currentCase!.id!, fieldId, value)
			}
			Vue.delete(this.savingFields, fieldId)
		}, 1000)
	}

	private handleFieldAwaitingUpdate (fieldId: string | undefined) {
		if (fieldId && !this.updateCaseStateAfterChange) {
			this.setFieldAwaitingUpdate(fieldId)
		}
	}

	private isHoursField (field: CaseField): boolean {
		return !!this.recordAssignmentService?.fieldsByHours[field.field.id!]?.length
	}

	/**
	 * Determines if the specified field belongs to a hidden section.
	 * Returns true if displayAllSections is true, fieldsToDisplay and hiddenFields are defined and the given fieldId is included in hiddenFields; otherwise, returns false.
	 * @param fieldId - The id of the field to check.
	 */
	private isFieldHidden (fieldId: string): boolean {
		return this.displayAllSections &&
			this.fieldsToDisplay !== null &&
			this.hiddenFields !== null &&
			this.hiddenFields.includes(fieldId)
	}
}
